import React from "react";
import cx from "classnames";
import { ProductPrices } from "../../../common/pricesCalculation";
import { formatCurrency } from "../../utils/currency";
import { BiPackage } from "react-icons/bi";
import { LuPackageOpen } from "react-icons/lu";
import { FaMoneyBillWave, FaShippingFast } from "react-icons/fa";
import { RiExchangeLine, RiPriceTag3Line } from "react-icons/ri";
import ClickPopover, { ClickPopoverPlacement } from "./ClickPopover";

export default function PricesCalculation_Popover({
  prices,
  placement = "above",
  size = "base"
}: {
  prices: ProductPrices;
  showLabels?: boolean;
  showListingPrice?: boolean;
  placement?: ClickPopoverPlacement;
  size?: "base" | "small";
}) {
  const showSetExternalPrice = !prices.setExternalPrice.eq(prices.listingPriceIncludingVAT);
  const showListingPrice = !prices.listingPriceIncludingVAT.eq(prices.retailPriceIncludingVAT);
  const showExtraShippingPrice = !prices.extraShippingPriceIncludingVAT.eq(0);
  const showExtraFeePrice = !prices.extraFeeIncludingVAT.eq(0);

  const showLabels = size === "small" ? false : true;
  const showListingPriceInSummary = showListingPrice && size !== "small";

  return (
  <ClickPopover
      placement={placement}
      content={
        <>
        <div className="flex flex-row gap-2">
          <p className="text-gray-700 text-sm flex flex-row gap-2">
            <span className="bg-gray-100 p-1 rounded-md">Origin listing: {prices.listingLabel}</span>
            <span className="bg-gray-100 p-1 rounded-md">Shipping with: {prices.shippingServiceSelected?.service.serviceCode}</span>
            <span className="bg-gray-100 p-1 rounded-md">Fixed return cost of 0.60 cts</span>
          </p>
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex flex-col gap-2 text-center">
            <span className="text-gray-800 text-md ">
              <BiPackage size={20} className="inline-block mr-1" /> Product
            </span>
            <span className="text-gray-900 text-2xl">
              {formatCurrency(prices.productCostIncludingVAT.toNumber())}
            </span>
            <span className="text-gray-700 text-sm ">
              {formatCurrency(prices.productCostVAT.toNumber())}&nbsp;VAT
            </span>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <span className="text-gray-800 text-md ">
              <LuPackageOpen size={20} className="inline-block mr-1" /> Packing
            </span>
            <span className="text-gray-900 text-2xl">
              {formatCurrency(prices.packingCostIncludingVAT.toNumber())}
            </span>
            <span className="text-gray-700 text-sm ">
              {formatCurrency(prices.packingCostVAT.toNumber())}&nbsp;VAT
            </span>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <span className="text-gray-800 text-md ">
              <FaMoneyBillWave size={20} className="inline-block mr-1" /> Margin
            </span>
            <span className="text-gray-900 text-2xl">
              {formatCurrency(prices.grossMarginIncludingVAT.toNumber())}
            </span>
            <span className="text-gray-700 text-sm ">
              {formatCurrency(prices.grossMarginVAT.toNumber())}&nbsp;VAT
            </span>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <span className="text-gray-800 text-md ">
              <RiExchangeLine size={20} className="inline-block mr-1" /> Channel
            </span>
            <span className="text-gray-900 text-2xl">
              {formatCurrency(prices.channelFeeIncludingVAT.toNumber())}
            </span>
            <span className="text-gray-700 text-sm ">
              {formatCurrency(prices.channelFeeVAT.toNumber())}&nbsp;VAT
            </span>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <span className="text-gray-800 text-md ">
              <FaShippingFast size={20} className="inline-block mr-1" /> Shipping
            </span>
            <span className="text-gray-900 text-2xl">
              {formatCurrency(prices.shippingCostIncludingVAT.toNumber())}
            </span>
            <span className="text-gray-700 text-sm ">
              {formatCurrency(prices.shippingCostVAT.toNumber())}&nbsp;VAT
            </span>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <span className="text-gray-800 text-md ">
              <FaShippingFast size={20} className="inline-block mr-1" style={{ transform: 'scaleX(-1)' }} /> Return
            </span>
            <span className="text-gray-900 text-2xl">
              {formatCurrency(prices.returnShippingCostIncludingVAT.toNumber())}
            </span>
            <span className="text-gray-700 text-sm ">
              {formatCurrency(prices.returnShippingCostVAT.toNumber())}&nbsp;VAT
            </span>
          </div>
          {showSetExternalPrice && (
            <div className="flex flex-col gap-2 text-center">
              <span className="text-gray-800 text-md">
                <RiPriceTag3Line size={20} className="inline-block mr-1" /> Set External Price
              </span>
              <span className="text-gray-900 text-2xl">
                {formatCurrency(prices.setExternalPrice.toNumber())}
              </span>
            </div>
          )}
          {showListingPrice && (
            <div className="flex flex-col gap-2 text-center">
              <span className="text-gray-800 text-md">
                <RiPriceTag3Line size={20} className="inline-block mr-1" /> Listing
              </span>
              <span className="text-gray-900 text-2xl">
                {formatCurrency(prices.listingPriceIncludingVAT.toNumber())}
              </span>
              <span className="text-gray-700 text-sm ">
                {formatCurrency(prices.listingPriceVAT.toNumber())}&nbsp;VAT
              </span>
            </div>
          )}
          {showExtraShippingPrice && (
            <div className="flex flex-col gap-2 text-center">
              <span className="text-gray-800 text-md text-ellipsis">
                <RiPriceTag3Line size={20} className="inline-block mr-1" /> Extra Shipping
              </span>
              <span className="text-gray-900 text-2xl">
                {formatCurrency(prices.extraShippingPriceIncludingVAT.toNumber())}
              </span>
              <span className="text-gray-700 text-sm ">
                {formatCurrency(prices.extraShippingPriceVAT.toNumber())}&nbsp;VAT
              </span>
            </div>
          )}
          {showExtraFeePrice && (
            <div className="flex flex-col gap-2 text-center">
              <span className="text-gray-800 text-md">
                <RiPriceTag3Line size={20} className="inline-block mr-1" /> Extra Fee
              </span>
              <span className="text-gray-900 text-2xl">
                {formatCurrency(prices.extraFeeIncludingVAT.toNumber())}
              </span>
              <span className="text-gray-700 text-sm ">
                {formatCurrency(prices.extraFeeVAT.toNumber())}&nbsp;VAT
              </span>
            </div>
          )}
          <div className="flex flex-col gap-2 text-center">
            <span className="text-gray-800 text-md">
              <RiPriceTag3Line size={20} className="inline-block mr-1" /> Retail
            </span>
            <span className="text-gray-900 text-2xl">
              {formatCurrency(prices.retailPriceIncludingVAT.toNumber())}
            </span>
            <span className="text-gray-700 text-sm ">
              {formatCurrency(prices.retailPriceVAT.toNumber())}&nbsp;VAT
            </span>
          </div>
        </div>
        </>
      }
    >
      <div className="flex flex-row gap-2 items-start align-start">
        {showListingPriceInSummary && <div className="flex flex-col gap-0">
          {showLabels && 
            <span className="text-gray-800 text-base inline-block">
              <RiPriceTag3Line size={20} className="inline-block mr-1" /> Listing
            </span>
          }
          <p
            className={cx(
              "text-gray-900 mt-0 mb-0",
              size === "base" ? "text-xl" : "text-2xl"
            )}
          >
            {formatCurrency(prices.listingPriceIncludingVAT.toNumber())}
          </p>
        </div>}
        <div className="flex flex-col gap-0">
          {showLabels && 
            <span className="text-gray-800 text-base inline-block">
              <RiPriceTag3Line size={20} className="inline-block mr-1" /> Paid
            </span>
          }
          <p
            className={cx(
              "text-gray-900 mt-0 mb-0",
              size === "small" ? "text-xl" : "text-2xl"
            )}
          >
            {formatCurrency(prices.retailPriceIncludingVAT.toNumber())}
          </p>
        </div>
        <div className="flex flex-col gap-0">
          {showLabels && (
            <span className="text-gray-800 text-base inline-block">
              <FaMoneyBillWave size={20} className="inline-block mr-1" /> Margin
            </span>
          )}
          <p className={cx(
            "text-green-700 mt-0 mb-0",
            size === "small" ? "text-xl" : "text-2xl"
          )}>
            {formatCurrency(prices.grossMarginIncludingVAT.toNumber())}&nbsp;
            <span className="text-sm">
              ({prices.grossMarginIncludingVAT.div(prices.retailPriceIncludingVAT.gt(0) ? prices.retailPriceIncludingVAT : 1).mul(100).round(0).toNumber()}%)
            </span>
          </p>
        </div>
      </div>
    </ClickPopover>
  );
}